import { useState } from 'react';
import {
  Typography,
  Table,
  Input,
  Select,
  Space,
  DatePicker,
  Row,
  Col,
  Button,
  Tag,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuditLogs } from '@hooks/useAdmin';
import type { AuditLogItem, AuditLogFilters } from '@/types/admin';

const { RangePicker } = DatePicker;

const acqActionColors: Record<string, string> = {
  Create: 'green',
  Update: 'blue',
  Delete: 'red',
  SoftDelete: 'red',
};

const acqEntityOptions = [
  { label: 'Product', value: 'Product' },
  { label: 'ProductBatch', value: 'ProductBatch' },
  { label: 'PurchaseOrder', value: 'PurchaseOrder' },
  { label: 'SalesOrder', value: 'SalesOrder' },
  { label: 'Customer', value: 'Customer' },
  { label: 'Supplier', value: 'Supplier' },
  { label: 'Invoice', value: 'Invoice' },
  { label: 'ServiceRequest', value: 'ServiceRequest' },
  { label: 'User', value: 'User' },
  { label: 'Recall', value: 'Recall' },
];

export default function AuditLogPage() {
  const [acqFilters, setMdFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 25,
  });
  const [acqSearchText, setMdSearchText] = useState('');
  const [acqDateRange, setMdDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const { data: acqAuditData, isLoading: acqIsLoading } = useAuditLogs(acqFilters);

  const acqHandleSearch = () => {
    setMdFilters((acqPrev) => ({ ...acqPrev, search: acqSearchText || undefined, page: 1 }));
  };

  const acqHandleEntityFilter = (acqEntity: string | undefined) => {
    setMdFilters((acqPrev) => ({ ...acqPrev, entityName: acqEntity, page: 1 }));
  };

  const acqHandleDateChange = (acqDates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setMdDateRange(acqDates);
    const acqFrom = acqDates?.[0]?.startOf('day').toISOString();
    const acqTo = acqDates?.[1]?.endOf('day').toISOString();
    setMdFilters((acqPrev) => ({
      ...acqPrev,
      dateFrom: acqFrom ?? undefined,
      dateTo: acqTo ?? undefined,
      page: 1,
    }));
  };

  const acqGetRowClassName = (acqRecord: AuditLogItem): string => {
    if (acqRecord.action === 'Create') return 'audit-row-create';
    if (acqRecord.action === 'Delete' || acqRecord.action === 'SoftDelete') return 'audit-row-delete';
    return '';
  };

  const acqColumns: ColumnsType<AuditLogItem> = [
    {
      title: 'Час',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (acqTs: string) => dayjs(acqTs).format('DD.MM.YYYY HH:mm:ss'),
    },
    {
      title: 'Користувач',
      dataIndex: 'userName',
      key: 'userName',
      width: 140,
      render: (acqName: string | undefined) => acqName ?? '—',
    },
    {
      title: 'Сутність',
      dataIndex: 'entityName',
      key: 'entityName',
      width: 140,
    },
    {
      title: 'ID',
      dataIndex: 'entityId',
      key: 'entityId',
      width: 120,
      ellipsis: true,
      render: (acqId: string | undefined) => acqId ?? '—',
    },
    {
      title: 'Дія',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (acqAction: string) => (
        <Tag color={acqActionColors[acqAction] ?? 'default'}>{acqAction}</Tag>
      ),
    },
    {
      title: 'Поле',
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: 140,
      render: (acqVal: string | undefined) => acqVal ?? '—',
    },
    {
      title: 'Старе значення',
      dataIndex: 'oldValue',
      key: 'oldValue',
      width: 180,
      ellipsis: true,
      render: (acqVal: string | undefined) => acqVal ?? '—',
    },
    {
      title: 'Нове значення',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 180,
      ellipsis: true,
      render: (acqVal: string | undefined) => acqVal ?? '—',
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (acqIp: string | undefined) => acqIp ?? '—',
    },
  ];

  return (
    <>
      <style>{`
        .audit-row-create { background-color: #f6ffed; }
        .audit-row-delete { background-color: #fff2f0; }
      `}</style>

      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Журнал аудиту
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Input
              placeholder="Пошук..."
              prefix={<SearchOutlined />}
              value={acqSearchText}
              onChange={(acqE) => setMdSearchText(acqE.target.value)}
              onPressEnter={acqHandleSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Сутність"
              allowClear
              style={{ width: 180 }}
              onChange={acqHandleEntityFilter}
              options={acqEntityOptions}
            />
            <RangePicker
              value={acqDateRange}
              onChange={acqHandleDateChange}
              format="DD.MM.YYYY"
              placeholder={['Від', 'До']}
            />
            <Button onClick={acqHandleSearch}>Шукати</Button>
          </Space>
        </Col>
      </Row>

      <Table<AuditLogItem>
        rowKey="id"
        columns={acqColumns}
        dataSource={acqAuditData?.items ?? []}
        loading={acqIsLoading}
        rowClassName={acqGetRowClassName}
        pagination={{
          current: acqFilters.page,
          pageSize: acqFilters.pageSize,
          total: acqAuditData?.totalCount ?? 0,
          showSizeChanger: true,
          showTotal: (acqTotal) => `Всього: ${acqTotal}`,
          onChange: (acqPage, acqSize) =>
            setMdFilters((acqPrev) => ({ ...acqPrev, page: acqPage, pageSize: acqSize })),
        }}
        scroll={{ x: 1400 }}
        size="middle"
      />
    </>
  );
}
